import * as Application from 'expo-application';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { MMKV } from 'react-native-mmkv';
import { APP_CONSTANTS } from '@/config/constants';
import { globalState } from '@/state/globalState';
import { getStableRecoveryDeviceId } from '@/services/recovery/RecoveryRuntimeGate';
import { supabase } from '@/services/supabase';
import { getRuntimeClientBuild, parseClientBuild } from './clientBuild';
import { collectClientInstallationMetadata } from './clientInstallationMetadata';

export type ClientInstallationSyncStatus = 'success' | 'partial' | 'error';

export type PresenceMetadata = {
  appVersion: string;
  nativeBuild: number | null;
  compatibilityBuild: number;
  platform: 'web' | 'android' | 'ios';
};

type StoredPresence = PresenceMetadata & {
  lastReportedAt: number;
  lastSyncStatus: ClientInstallationSyncStatus | null;
};

export type PresenceDecisionInput = {
  now: number;
  previous: StoredPresence | null;
  current: PresenceMetadata;
  syncStatus?: ClientInstallationSyncStatus;
  throttleMs?: number;
};

const PRESENCE_STORAGE_ID = 'app-guincho-client-installation-presence';
const PRESENCE_THROTTLE_MS = 6 * 60 * 60 * 1000;
const storage = new MMKV({ id: PRESENCE_STORAGE_ID });
let reportQueue: Promise<boolean> = Promise.resolve(false);

const parseStoredPresence = (value?: string): StoredPresence | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<StoredPresence>;
    if (
      typeof parsed.lastReportedAt !== 'number'
      || typeof parsed.appVersion !== 'string'
      || typeof parsed.compatibilityBuild !== 'number'
      || !['web', 'android', 'ios'].includes(String(parsed.platform))
    ) return null;
    return parsed as StoredPresence;
  } catch {
    return null;
  }
};

export const shouldReportClientInstallation = ({
  now,
  previous,
  current,
  syncStatus,
  throttleMs = PRESENCE_THROTTLE_MS,
}: PresenceDecisionInput): boolean =>
  !previous
  || previous.appVersion !== current.appVersion
  || previous.nativeBuild !== current.nativeBuild
  || previous.compatibilityBuild !== current.compatibilityBuild
  || previous.platform !== current.platform
  || (syncStatus !== undefined && previous.lastSyncStatus !== syncStatus)
  || now - previous.lastReportedAt >= throttleMs;

const resolveMetadata = (): PresenceMetadata | null => {
  const platform = Platform.OS;
  if (platform !== 'web' && platform !== 'android' && platform !== 'ios') return null;
  const compatibilityBuild = getRuntimeClientBuild();
  if (compatibilityBuild <= 0) return null;
  return {
    appVersion: Constants.expoConfig?.version ?? APP_CONSTANTS.VERSION,
    nativeBuild: platform === 'web' ? null : parseClientBuild(Application.nativeBuildVersion),
    compatibilityBuild,
    platform,
  };
};

/** Metadata usada pela presença e pela tentativa pós-atualização do sync. */
export const getClientInstallationPresenceMetadata = (): PresenceMetadata | null => resolveMetadata();

const getPresenceScope = (): string => JSON.stringify([
  globalState.user.user.get()?.id,
  globalState.user.profile.get()?.id,
  globalState.user.profile.get()?.empresa_id,
  globalState.company.data.get()?.id,
  getStableRecoveryDeviceId(),
  Platform.OS,
]);

const reportOnce = async (syncStatus?: ClientInstallationSyncStatus, requestedScope = getPresenceScope()): Promise<boolean> => {
  if (requestedScope !== getPresenceScope() || !globalState.network.isConnected.get()) return false;
  const user = globalState.user.user.get();
  const profile = globalState.user.profile.get();
  const company = globalState.company.data.get();
  const metadata = resolveMetadata();
  if (
    !user?.id
    || profile?.id !== user.id
    || !profile.empresa_id
    || company?.id !== profile.empresa_id
    || !metadata
  ) return false;

  const deviceId = getStableRecoveryDeviceId();
  const storageKey = `${profile.empresa_id}:${user.id}:${deviceId}:${metadata.platform}`;
  const now = Date.now();
  const previous = parseStoredPresence(storage.getString(storageKey));
  if (!shouldReportClientInstallation({ now, previous, current: metadata, syncStatus })) return false;

  const deviceMetadata = await collectClientInstallationMetadata({ companyId: profile.empresa_id, userId: user.id }).catch(() => null);
  if (requestedScope !== getPresenceScope() || !globalState.network.isConnected.get()) return false;
  const params = {
    p_device_id: deviceId,
    p_platform: metadata.platform,
    p_app_version: metadata.appVersion,
    p_native_build: metadata.nativeBuild,
    p_compatibility_build: metadata.compatibilityBuild,
    p_sync_status: syncStatus ?? null,
  };
  let result = await supabase.rpc('app_guincho_touch_client_installation_v2', { ...params, p_device_metadata: deviceMetadata });
  if (requestedScope !== getPresenceScope()) return false;
  // A implantação do cliente pode preceder a migração; falhas reais não são mascaradas.
  if (result.error?.code === 'PGRST202') {
    result = await supabase.rpc('app_guincho_touch_client_installation_v1', params);
  }
  const { data, error } = result;
  if (requestedScope !== getPresenceScope()) return false;
  if (error || data !== true) throw error ?? new Error('client_installation_presence_failed');

  storage.set(storageKey, JSON.stringify({
    ...metadata,
    lastReportedAt: now,
    lastSyncStatus: syncStatus ?? previous?.lastSyncStatus ?? null,
  } satisfies StoredPresence));
  return true;
};

export const reportClientInstallationPresence = (
  syncStatus?: ClientInstallationSyncStatus
): Promise<boolean> => {
  const requestedScope = getPresenceScope();
  const next = reportQueue.catch(() => false).then(() => reportOnce(syncStatus, requestedScope));
  reportQueue = next;
  return next;
};

export const reportClientInstallationPresenceBestEffort = (
  syncStatus?: ClientInstallationSyncStatus
): void => {
  void reportClientInstallationPresence(syncStatus).catch(() => undefined);
};
